import React, { useEffect, useRef, useState } from "react";
import { APP_IDS } from "../constants/testIds";
import { STATUS } from "../constants/colors";
import { PhoneField, validatePhone } from "./PhoneField";
import { TenderForm } from "./TenderForm";

export const TicketModal = ({
  number,
  ticket,
  currentUser,
  users,
  onClose,
  onRelease,
  onMarkSold,
  onMarkFundsReceived,
  onRecord,
  onUpdateBuyer,
  onResendEmail,
  onAssignSeller,
  onUnassignSeller,
  getHistory,
  showToast,
}) => {
  const closeRef = useRef(null);
  const [history, setHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [sellForm, setSellForm] = useState({ buyer: "", phone: "", email: "", address: "", action: "sold", assignSellerId: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [tenderOpen, setTenderOpen] = useState(false);
  // For the "Mark Funds Received" flow on an available ticket (admin-only):
  // toggles the inline tender form below the Sell/Reserve buttons.
  const [sellTenderOpen, setSellTenderOpen] = useState(false);
  // Inline "Reassign to another seller" affordance (admin-only, on any status)
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignPick, setReassignPick] = useState("");
  const [reassignBusy, setReassignBusy] = useState(false);

  useEffect(() => {
    const prev = document.activeElement;
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      try { prev?.focus(); } catch (focusErr) {
        // eslint-disable-next-line no-console
        console.warn("Could not restore focus on modal close:", focusErr?.message);
      }
    };
  }, [onClose]);

  useEffect(() => {
    let active = true;
    if (number != null && getHistory) {
      setHistoryLoaded(false);
      getHistory(number).then((res) => {
        if (!active) return;
        setHistory(res.history || []);
        setHistoryLoaded(true);
      });
    }
    return () => { active = false; };
  }, [number, getHistory]);

  // Manual prior-buyer fill — user opts in by clicking the returning-buyer hint button.
  const fillPriorBuyer = () => {
    const mostRecent = history[0]; // sorted year DESC
    setSellForm((f) => ({
      ...f,
      buyer: ticket.prior_buyer_name || f.buyer,
      phone: mostRecent?.phone || ticket.prior_phone || f.phone,
    }));
  };

  // Default the seller-picker to the current admin if they're recording on an unassigned ticket.
  // Hook must run unconditionally — gate logic with the computed flags below.
  const isAdminForPicker = currentUser?.role === "admin";
  const isUnassignedForPicker = ticket ? !ticket.assigned_seller_id : false;
  const showPickerForEffect = isAdminForPicker && ticket?.status === "available" && isUnassignedForPicker;
  useEffect(() => {
    if (showPickerForEffect && !sellForm.assignSellerId && currentUser?.id) {
      setSellForm((f) => ({ ...f, assignSellerId: currentUser.id }));
    }
  }, [showPickerForEffect, currentUser?.id, sellForm.assignSellerId]);

  if (number == null || !ticket) return null;

  const canEdit = !!ticket.can_edit;
  const isAdmin = currentUser?.role === "admin";
  const status = ticket.status;
  const statusMeta = STATUS[status] || STATUS.available;
  const isUnassigned = !ticket.assigned_seller_id;
  const showSellerPicker = isAdmin && status === "available" && isUnassigned;
  const sellerOptions = (users || []).filter((u) => u.role === "admin" || u.role === "seller");

  const canSellThisTicket =
    status === "available" && (isAdmin || ticket.assigned_seller_id === currentUser?.id);
  const canReleaseThisTicket =
    status !== "available" && canEdit && (status !== "funds_received" || isAdmin);

  const submitSell = async (e, opts = {}) => {
    e?.preventDefault?.();
    const action = opts.action || sellForm.action;
    const tenderOverride = opts.tender || null;
    setError("");
    if (!sellForm.buyer.trim()) { setError("Buyer name is required"); return; }
    const phoneErr = validatePhone(sellForm.phone);
    if (phoneErr) { setError(phoneErr); return; }
    if (showSellerPicker && !sellForm.assignSellerId) { setError("Pick who to credit this sale to."); return; }
    setBusy(true);
    const res = await onRecord({
      number,
      buyer: sellForm.buyer.trim(),
      phone: sellForm.phone.trim(),
      email: sellForm.email.trim(),
      address: sellForm.address.trim(),
      action,
      assign_to_seller_id: showSellerPicker ? sellForm.assignSellerId : undefined,
      tender: tenderOverride,
    });
    setBusy(false);
    if (!res.ok) { setError(res.error || "Could not record."); return; }
    const statusLabel = res.status === "funds_received" ? "funds received" : res.status;
    const tenderTail = tenderOverride
      ? ` (${tenderOverride.tender_type === "check" ? `Check #${tenderOverride.check_number}` : "Cash"} $${tenderOverride.tender_amount})`
      : "";
    showToast?.(`Ticket #${number} recorded as ${statusLabel}${tenderTail}${res.assigned_seller_name ? ` — credited to ${res.assigned_seller_name}` : ""}`);
    onClose();
  };

  // Wraps submitSell for the inline "Mark Funds Received" path: passes action + tender explicitly.
  const submitSellWithTender = (tender) => submitSell(null, { action: "funds_received", tender });

  const doFundsReceived = async (tender) => {
    setBusy(true);
    const res = await onMarkFundsReceived(number, tender);
    setBusy(false);
    if (!res.ok) { setError(res.error || "Could not mark funds received."); return; }
    const t = tender ? ` (${tender.tender_type === "check" ? `Check #${tender.check_number}` : "Cash"} $${tender.tender_amount})` : "";
    showToast?.(`Ticket #${number} — funds received${t}`);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
      role="dialog" aria-modal="true" aria-label={`Ticket ${number}`} data-testid={APP_IDS.modal}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ background: "rgba(32, 31, 30, 0.55)" }}
    >
      <div className="rounded-lg w-full max-w-md border shadow-lg my-8" style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", boxShadow: "var(--shadow-modal)" }}>
        <div className="px-6 pt-5 pb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold" style={{ color: "var(--ink-primary)" }}>Ticket #{number}</h2>
          <span
            data-testid="ticket-modal-status"
            className="text-xs font-semibold px-2.5 py-1 rounded-full"
            style={{ background: statusMeta.bg, color: statusMeta.text, boxShadow: `inset 0 0 0 1px ${statusMeta.outline}` }}
          >
            {statusMeta.label}
          </span>
        </div>

        {/* Trophy badge — ADMIN ONLY per user preference; shows any drawings this ticket won this year */}
        {isAdmin && ticket.wins && ticket.wins.length > 0 && (
          <div className="mx-6 mb-3 rounded-md border p-3" style={{ borderColor: "var(--semantic-warning-soft)", background: "var(--surface-subtle)" }} data-testid="ticket-wins-panel">
            <div className="flex items-start gap-2">
              <span aria-hidden="true" className="text-lg leading-none mt-0.5">🏆</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--semantic-warning)", letterSpacing: "0.04em" }}>
                  Prize winner
                </p>
                <ul className="space-y-1">
                  {ticket.wins.map((w) => (
                    <li key={`${w.drawing_date}-${w.name}`} className="text-sm" style={{ color: "var(--semantic-warning)" }}>
                      <strong>{w.name}</strong>
                      <span className="ml-1.5 text-xs opacity-80">
                        · {w.drawing_date} · ${Number(w.prize_amount).toFixed(2)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="px-6 pb-4 text-sm" style={{ color: "var(--ink-primary)" }}>
          <dl className="grid grid-cols-3 gap-y-2 gap-x-3">
            <dt style={{ color: "var(--ink-tertiary)" }}>Assigned to</dt>
            <dd className="col-span-2">
              {ticket.assigned_seller_name ? (
                <>
                  <span className="font-medium">{ticket.assigned_seller_name}</span>
                  {ticket.assigned_seller_phone && (
                    <span className="ml-2 text-xs" style={{ color: "var(--ink-tertiary)" }} data-testid="modal-seller-phone">
                      · {ticket.assigned_seller_phone}
                    </span>
                  )}
                </>
              ) : (
                <span style={{ color: "var(--ink-tertiary)" }}>Unassigned</span>
              )}
              {isAdmin && onAssignSeller && !reassignOpen && (
                <button
                  type="button"
                  data-testid="modal-reassign-open"
                  onClick={() => {
                    setReassignPick(ticket.assigned_seller_id || "");
                    setReassignOpen(true);
                  }}
                  className="ml-3 text-xs font-semibold underline hover:no-underline"
                  style={{ color: "var(--brand-primary)" }}
                >
                  {ticket.assigned_seller_id ? "Reassign" : "Assign"}
                </button>
              )}
              {isAdmin && onAssignSeller && reassignOpen && (
                <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="modal-reassign-controls">
                  <select
                    data-testid="modal-reassign-select"
                    value={reassignPick}
                    onChange={(e) => setReassignPick(e.target.value)}
                    disabled={reassignBusy}
                    className="text-sm rounded-md border px-2 py-1"
                    style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-primary)" }}
                  >
                    <option value="">— Unassigned —</option>
                    {(users || [])
                      .filter((u) => u.role === "seller" || u.role === "admin")
                      .map((u) => (
                        <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                      ))}
                  </select>
                  <button
                    type="button"
                    data-testid="modal-reassign-save"
                    disabled={reassignBusy || reassignPick === (ticket.assigned_seller_id || "")}
                    onClick={async () => {
                      setReassignBusy(true);
                      let res;
                      if (!reassignPick) {
                        res = await onUnassignSeller?.(number);
                      } else {
                        res = await onAssignSeller?.(number, reassignPick);
                      }
                      setReassignBusy(false);
                      if (res?.ok === false) {
                        showToast?.(res.error || "Could not update assignment");
                        return;
                      }
                      const label = reassignPick
                        ? (users || []).find((u) => u.id === reassignPick)?.name || "seller"
                        : "unassigned";
                      showToast?.(`Ticket #${number} ${reassignPick ? `assigned to ${label}` : "unassigned"}`);
                      setReassignOpen(false);
                    }}
                    className="px-3 py-1 rounded-md text-xs font-semibold text-white disabled:opacity-50"
                    style={{ background: "var(--brand-primary)" }}
                  >
                    {reassignBusy ? "Saving…" : "Save"}
                  </button>
                  <button
                    type="button"
                    data-testid="modal-reassign-cancel"
                    disabled={reassignBusy}
                    onClick={() => setReassignOpen(false)}
                    className="px-2 py-1 rounded-md text-xs font-medium border disabled:opacity-50"
                    style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-primary)" }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </dd>
            {status !== "available" && (
              <>
                <dt style={{ color: "var(--ink-tertiary)" }}>Buyer</dt>
                <dd className="col-span-2 font-medium">{canEdit ? (ticket.buyer || "—") : <span style={{ color: "var(--ink-tertiary)" }}>Hidden</span>}</dd>
                <dt style={{ color: "var(--ink-tertiary)" }}>Phone</dt>
                <dd className="col-span-2">{canEdit ? (ticket.phone || "—") : <span style={{ color: "var(--ink-tertiary)" }}>Hidden</span>}</dd>
                {canEdit && (ticket.email || isAdmin) && (
                  <>
                    <dt style={{ color: "var(--ink-tertiary)" }}>Email</dt>
                    <dd className="col-span-2" data-testid="modal-buyer-email">
                      {ticket.email ? ticket.email : <span className="italic" style={{ color: "var(--ink-tertiary)" }}>Not on file</span>}
                    </dd>
                  </>
                )}
                {canEdit && (ticket.address || isAdmin) && (
                  <>
                    <dt style={{ color: "var(--ink-tertiary)" }}>Address</dt>
                    <dd className="col-span-2" data-testid="modal-buyer-address">
                      {ticket.address ? ticket.address : <span className="italic" style={{ color: "var(--ink-tertiary)" }}>Not on file</span>}
                    </dd>
                  </>
                )}
                <dt style={{ color: "var(--ink-tertiary)" }}>Sold by</dt>
                <dd className="col-span-2">{canEdit ? (ticket.seller || "—") : <span style={{ color: "var(--ink-tertiary)" }}>Hidden</span>}</dd>
                <dt style={{ color: "var(--ink-tertiary)" }}>Date</dt>
                <dd className="col-span-2">{canEdit ? (ticket.date || "—") : <span style={{ color: "var(--ink-tertiary)" }}>Hidden</span>}</dd>
                {status === "funds_received" && canEdit && (
                  <>
                    <dt style={{ color: "var(--ink-tertiary)" }}>Payment</dt>
                    <dd className="col-span-2 font-medium" data-testid="modal-tender">
                      {ticket.tender_type
                        ? (
                          <span>
                            <span className="capitalize">{ticket.tender_type}</span>
                            {ticket.tender_type === "check" && ticket.check_number && (
                              <span style={{ color: "var(--ink-tertiary)" }}> · #{ticket.check_number}</span>
                            )}
                            {ticket.tender_amount > 0 && (
                              <span className="ml-2 font-semibold" style={{ color: "var(--brand-primary)" }}>${Number(ticket.tender_amount).toFixed(2)}</span>
                            )}
                          </span>
                        )
                        : <span className="italic" style={{ color: "var(--ink-tertiary)" }}>Not recorded</span>}
                    </dd>
                  </>
                )}
              </>
            )}
          </dl>
          {status !== "available" && !canEdit && !isAdmin && (
            <p className="mt-3 text-xs" style={{ color: "var(--ink-tertiary)" }}>You can only see buyer details for tickets assigned to you.</p>
          )}
          {/* Admin action row for existing sold/reserved/funds_received tickets */}
          {isAdmin && ["sold", "reserved", "funds_received"].includes(status) && (
            <BuyerAdminActions
              ticket={ticket}
              number={number}
              onUpdateBuyer={onUpdateBuyer}
              onResendEmail={onResendEmail}
              showToast={showToast}
            />
          )}
        </div>

        {/* Past buyers panel */}
        {historyLoaded && history.length > 0 && (
          <div className="px-6 pb-4" data-testid="modal-past-buyers">
            <h3 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--ink-tertiary)", letterSpacing: "0.04em" }}>
              Past buyers
            </h3>
            <ul className="space-y-1.5">
              {history.map((h) => (
                <li key={h.year} className="flex items-baseline justify-between gap-3 text-sm">
                  <span>
                    <span className="font-semibold mr-2" style={{ color: "var(--brand-primary)" }}>{h.year}</span>
                    <span style={{ color: "var(--ink-primary)" }}>{h.buyer || "—"}</span>
                  </span>
                  {h.phone && (
                    <span className="text-xs" style={{ color: "var(--ink-tertiary)" }}>{h.phone}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Inline sell form (only for available tickets you own / admin) */}
        {canSellThisTicket && (
          <form onSubmit={submitSell} className="px-6 pb-4 border-t pt-4 space-y-3" style={{ borderColor: "var(--border-default)" }} data-testid="modal-sell-form">
            <h3 className="text-sm font-semibold" style={{ color: "var(--ink-primary)" }}>Sell this ticket</h3>
            {ticket.has_prior_buyer && ticket.prior_buyer_name && (
              <button
                type="button"
                onClick={fillPriorBuyer}
                data-testid="modal-returning-buyer-hint"
                className="w-full text-left text-xs px-3 py-2 rounded-md transition-colors flex items-center justify-between gap-3 group"
                style={{ background: "var(--semantic-warning-soft)", color: "var(--semantic-warning)", border: "1px solid var(--semantic-warning-soft)" }}
              >
                <span>
                  Returning buyer from {ticket.prior_buyer_year}: <strong>{ticket.prior_buyer_name}</strong> — offer first.
                </span>
                <span
                  className="text-xs font-semibold px-2 py-1 rounded-md whitespace-nowrap transition-colors"
                  style={{ background: "var(--semantic-warning-soft)", color: "var(--semantic-warning)" }}
                >
                  Use this name →
                </span>
              </button>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                data-testid="modal-sell-buyer"
                value={sellForm.buyer}
                onChange={(e) => setSellForm({ ...sellForm, buyer: e.target.value })}
                placeholder="Buyer name"
                className="rounded-md border px-3 py-2 text-base focus:outline-none focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
                style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-primary)" }}
              />
              <PhoneField
                id={`modal-sell-phone-${number}`}
                value={sellForm.phone}
                onChange={(v) => setSellForm({ ...sellForm, phone: v })}
                testId="modal-sell-phone"
                showLabel={false}
                helper={null}
                placeholder="Buyer phone (required)"
              />
            </div>
            <p className="text-xs -mt-1" style={{ color: "var(--ink-tertiary)" }}>
              Phone is required. If the buyer doesn&apos;t share, enter your own number.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                data-testid="modal-sell-email"
                type="email"
                value={sellForm.email}
                onChange={(e) => setSellForm({ ...sellForm, email: e.target.value })}
                placeholder="Buyer email (optional — enables e-ticket)"
                className="rounded-md border px-3 py-2 text-base focus:outline-none focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
                style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-primary)" }}
                autoComplete="off"
              />
              <input
                data-testid="modal-sell-address"
                value={sellForm.address}
                onChange={(e) => setSellForm({ ...sellForm, address: e.target.value })}
                placeholder="Mailing address (optional)"
                className="rounded-md border px-3 py-2 text-base focus:outline-none focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
                style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-primary)" }}
                autoComplete="off"
              />
            </div>
            <p className="text-xs -mt-1" style={{ color: "var(--ink-tertiary)" }}>
              Email triggers an electronic ticket. Address is used only if we mail a printed ticket.
            </p>
            {showSellerPicker && (
              <div data-testid="modal-assign-seller-block">
                <label className="text-xs font-medium block mb-1" style={{ color: "var(--ink-tertiary)" }}>
                  Credit sale to <span style={{ color: "var(--semantic-danger)" }}>*</span>
                </label>
                <select
                  data-testid="modal-assign-seller"
                  value={sellForm.assignSellerId}
                  onChange={(e) => setSellForm({ ...sellForm, assignSellerId: e.target.value })}
                className="w-full rounded-md border px-3 py-2 text-base focus:outline-none focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
                  style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-primary)" }}
                >
                  {sellerOptions.map((u) => {
                    const label = `${u.name}${u.id === currentUser?.id ? " (me)" : ""} · ${u.role}`;
                    return (
                      <option key={u.id} value={u.id}>{label}</option>
                    );
                  })}
                </select>
                <p className="text-xs mt-1" style={{ color: "var(--ink-tertiary)" }}>
                  This ticket isn&apos;t assigned to anyone — pick who should be credited.
                </p>
              </div>
            )}
            {error && <p className="text-sm font-medium" style={{ color: "var(--semantic-danger)" }} role="alert">{error}</p>}
            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={busy || sellTenderOpen}
                data-testid="modal-sell-sold"
                onClick={() => setSellForm({ ...sellForm, action: "sold" })}
                className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors hover:opacity-90 disabled:opacity-50"
                style={{ background: "var(--brand-primary)" }}
              >
                {busy && sellForm.action === "sold" ? "Saving…" : "Mark Sold"}
              </button>
              <button
                type="submit"
                disabled={busy || sellTenderOpen}
                data-testid="modal-sell-reserved"
                onClick={() => setSellForm({ ...sellForm, action: "reserved" })}
                className="px-4 py-2 text-sm font-medium rounded-md transition-colors hover:opacity-90 disabled:opacity-50"
                style={{ background: STATUS.reserved.bg, color: STATUS.reserved.text }}
              >
                Reserve
              </button>
              {isAdmin && (
                <button
                  type="button"
                  disabled={busy || sellTenderOpen}
                  data-testid="modal-sell-funds-received"
                  onClick={() => {
                    // Light pre-validation before revealing tender so admin gets feedback fast.
                    setError("");
                    if (!sellForm.buyer.trim()) { setError("Buyer name is required"); return; }
                    const phoneErr = validatePhone(sellForm.phone);
                    if (phoneErr) { setError(phoneErr); return; }
                    if (showSellerPicker && !sellForm.assignSellerId) { setError("Pick who to credit this sale to."); return; }
                    setSellTenderOpen(true);
                  }}
                  className="px-4 py-2 text-sm font-medium rounded-md transition-colors hover:opacity-90 disabled:opacity-50"
                  style={{ background: STATUS.funds_received.bg, color: STATUS.funds_received.text }}
                >
                  Mark Funds Received
                </button>
              )}
            </div>

            {/* Inline tender capture (admin-only, only when "Mark Funds Received" was tapped) */}
            {sellTenderOpen && isAdmin && (
              <div className="mt-3 p-3 rounded-md border" style={{ borderColor: "var(--border-default)", background: "var(--surface-subtle)" }} data-testid="modal-sell-tender-block">
                <h4 className="text-sm font-semibold mb-1" style={{ color: "var(--ink-primary)" }}>Record payment</h4>
                <p className="text-xs mb-3" style={{ color: "var(--ink-tertiary)" }}>How did the buyer pay for this ticket?</p>
                <TenderForm
                  defaultAmount={100}
                  onSubmit={async (tender) => {
                    await submitSellWithTender(tender);
                  }}
                  onCancel={() => setSellTenderOpen(false)}
                  busy={busy}
                  applyLabel="Confirm & Mark Funds Received"
                />
              </div>
            )}
          </form>
        )}

        {/* Inline tender capture (when admin clicks Mark Funds Received) */}
        {tenderOpen && status === "sold" && isAdmin && (
          <div className="px-6 pb-4 border-t pt-4" style={{ borderColor: "var(--border-default)" }} data-testid="modal-tender-block">
            <h3 className="text-sm font-semibold mb-2" style={{ color: "var(--ink-primary)" }}>Record payment</h3>
            <p className="text-xs mb-3" style={{ color: "var(--ink-tertiary)" }}>How did the buyer pay for this ticket?</p>
            <TenderForm
              defaultAmount={100}
              onSubmit={doFundsReceived}
              onCancel={() => setTenderOpen(false)}
              busy={busy}
              applyLabel="Mark Funds Received"
            />
            {error && <p className="text-sm font-medium mt-2" style={{ color: "var(--semantic-danger)" }} role="alert">{error}</p>}
          </div>
        )}

        <div className="px-6 py-4 border-t flex flex-wrap items-center justify-end gap-2" style={{ borderColor: "var(--border-default)" }}>
          {status === "reserved" && canEdit && (
            <button data-testid={APP_IDS.modalMarkSold} onClick={() => onMarkSold(number)} className="px-5 py-2 text-sm font-medium text-white rounded-md transition-colors hover:opacity-90" style={{ background: "var(--semantic-success)" }}>Mark Sold</button>
          )}
          {status === "sold" && isAdmin && !tenderOpen && (
            <button
              data-testid="modal-mark-funds-received"
              onClick={() => { setError(""); setTenderOpen(true); }}
              disabled={busy}
              className="px-5 py-2 text-sm font-medium text-white rounded-md transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ background: STATUS.funds_received.bg }}
            >
              Mark Funds Received
            </button>
          )}
          {canReleaseThisTicket && (
            <button data-testid={APP_IDS.modalRelease} onClick={() => onRelease(number)} className="px-5 py-2 text-sm font-medium text-white rounded-md transition-colors hover:opacity-90" style={{ background: "var(--semantic-danger)" }}>Release</button>
          )}
          <button ref={closeRef} data-testid={APP_IDS.modalClose} onClick={onClose} className="px-5 py-2 text-sm font-medium rounded-md border transition-colors" style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-primary)" }}>Close</button>
        </div>
      </div>
    </div>
  );
};


// ---------- BuyerAdminActions ----------
// Admin-only footer inside the ticket details section. Two capabilities:
//  1. Resend the appropriate email (Sale Confirmation while sold, Electronic
//     Ticket while funds_received). Disabled if buyer has no email on file.
//  2. Inline "Edit buyer info" — allows adding an email/address AFTER the sale
//     so admin can then resend the electronic ticket to a buyer who initially
//     didn't provide one.
const BuyerAdminActions = ({ ticket, number, onUpdateBuyer, onResendEmail, showToast }) => {
  const [editing, setEditing] = React.useState(false);
  const [form, setForm] = React.useState({
    buyer: ticket.buyer || "",
    phone: ticket.phone || "",
    email: ticket.email || "",
    address: ticket.address || "",
  });
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    setForm({
      buyer: ticket.buyer || "",
      phone: ticket.phone || "",
      email: ticket.email || "",
      address: ticket.address || "",
    });
  }, [ticket.buyer, ticket.phone, ticket.email, ticket.address]);

  const doResend = async () => {
    setErr("");
    setBusy(true);
    const res = await onResendEmail?.(number);
    setBusy(false);
    if (res?.ok) {
      showToast?.(res.message || `Email re-sent to ${ticket.email}`);
    } else {
      // Never a 5xx anymore — the backend returns a helpful message inline.
      setErr(res?.error || "Could not send email.");
    }
  };

  const saveEdit = async () => {
    setErr("");
    setBusy(true);
    const res = await onUpdateBuyer?.(number, form);
    setBusy(false);
    if (res?.ok) {
      showToast?.("Buyer info updated.");
      setEditing(false);
    } else {
      setErr(res?.error || "Could not update buyer info.");
    }
  };

  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--border-default)" }} data-testid="modal-buyer-admin-actions">
      {!editing ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={doResend}
            disabled={busy || !ticket.email}
            data-testid="modal-resend-email"
            title={!ticket.email ? "Add an email first" : "Re-send the buyer email"}
            className="px-3 py-1.5 rounded-md text-xs font-semibold border disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ color: "var(--brand-primary)", background: "var(--surface-card)", borderColor: "var(--border-default)" }}
          >
            {busy ? "Sending\u2026" : ticket.status === "funds_received" ? "Re-send e-ticket" : "Re-send confirmation"}
          </button>
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid="modal-edit-buyer"
            className="px-3 py-1.5 rounded-md text-xs font-semibold border"
            style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-secondary)" }}
          >
            Edit buyer info
          </button>
          {err && <p className="w-full text-xs mt-1" style={{ color: "var(--semantic-danger)" }}>{err}</p>}
        </div>
      ) : (
        <div className="space-y-2" data-testid="modal-edit-buyer-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input data-testid="edit-buyer-name" value={form.buyer} onChange={(e) => setForm({ ...form, buyer: e.target.value })} placeholder="Name" className="rounded-md border px-3 py-1.5 text-sm focus:outline-none focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25" style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-primary)" }} />
            <input data-testid="edit-buyer-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="rounded-md border px-3 py-1.5 text-sm focus:outline-none focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25" style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-primary)" }} />
            <input data-testid="edit-buyer-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="rounded-md border px-3 py-1.5 text-sm focus:outline-none focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25" style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-primary)" }} />
            <input data-testid="edit-buyer-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Mailing address" className="rounded-md border px-3 py-1.5 text-sm focus:outline-none focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25" style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-primary)" }} />
          </div>
          {err && <p className="text-xs" style={{ color: "var(--semantic-danger)" }}>{err}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(false)} disabled={busy} className="px-3 py-1.5 rounded-md text-xs font-semibold border" style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-tertiary)" }}>Cancel</button>
            <button type="button" onClick={saveEdit} disabled={busy} data-testid="edit-buyer-save" className="px-3 py-1.5 rounded-md text-xs font-semibold text-white" style={{ background: "var(--brand-primary)" }}>
              {busy ? "Saving\u2026" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
